import React, { Fragment, useState } from "react"
import gql from "graphql-tag"
import styled from "@emotion/styled"
import { useQuery, useMutation } from "@apollo/client"
import { MaxWidth, Flex } from "../UI/Containers"
import { SubmitButton } from "../UI/Button"
import { Input, TextArea } from "../UI/Input"
import { LoadingPage } from "../UI"
import { prefixImageUrl } from "../utils/imageUrl"

export const FormContainer = styled("section")`
    padding: ${props => props.theme.spacing.default};
    text-align: center;
    margin: 2rem 0;

    .content {
        text-align: left;
        *:first-child {
          font-size: 2rem;
          font-family: ${props => props.theme.fonts.headings};
          margin-bottom: 1rem;
          padding: 0;
        }
    }
`
export const FormFlex = styled(Flex)`
    align-items: stretch;
    gap: 1rem;

    ${props => props.theme.bp.below.sm} {
        flex-wrap: wrap;
    }

    & > div {
        display: flex;
        flex-direction: column;
        width: 50%;
        flex: 1;
        ${props => props.theme.bp.below.sm} {
            width: 100%;
            flex: unset;
            margin-bottom: 1rem;
        }

        /* Style inputs in the first column (direct children) */
        &:first-of-type > * {
          margin-bottom: 1rem;
          &:last-of-type {
            margin-bottom: 0;
          }
        }

        /* Style textarea in the second column (direct child) */
        &:last-of-type > * {
          flex-grow: 1;
          height: 100%;
          min-height: 150px;
        }
    }
`

const DepartmentRow = styled("div")`
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
    width: 66.6%;
    white-space: nowrap;
    a {
        text-decoration: underline;
    }
    ${props => props.theme.bp.below.lg} {
        width: 100%;
        padding: 0;
        flex-direction: column;
    }
`

const FlexAlign = styled("div")`
    display: flex;
    width: 50%;
    justify-content: ${props => props.justify};
    ${props => props.theme.bp.below.lg} {
        width: 100%;
        display: block;
    }
    div {
        min-width: 300px;
        span,
        a {
            display: block;
        }
    }
`

const PeopleWrapper = styled("div")`
  display: flex;
  text-align: left;
  flex-grow: 1;
  line-height: 1.5;
  width: 100%;
  margin-bottom: 2rem;
  h2 {
    font-size: 1rem;
    white-space: nowrap;
    font-weight: 100;
  }
  a {
    text-decoration: underline;
  }
  h3 {
  font-size: 0.9rem;
  }
  .title {
    ${props => props.theme.bp.below.lg} {
      margin-bottom: 1rem;
    }
  }

  ${props => props.theme.bp.below.lg} {
    margin-bottom: 1rem;
    flex-direction: column;
    padding: 1rem;
  }


  .person {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;

    .image {
      border-radius: 100%;
      max-width: 120px;
      overflow: hidden;
      flex: 1;
      min-width: 0;

      img {
        max-width: 100%;
        height: auto;
        object-fit: cover;
        aspect-ratio: 1/1;
      }
    }
    .info {
      flex: none;
      min-width: 0;
      h2 {
        margin: 0;
      }
    }
  }
`

const DepartMentWrapper = styled("div")`
    display: flex;
    border-bottom: 1px solid black;
    margin-bottom: 2rem;
    &:last-of-type {
        border: none;
        margin-bottom: none;
    }
    h2,
    h3 {
        font-family: ${props => props.theme.fonts.body};
    }
    ${props => props.theme.bp.below.lg} {
        flex-direction: column;
        text-align: left;
    }
    & > h2 {
        font-size: 1rem;
        text-transform: uppercase;
        width: 33.3%;
        ${props => props.theme.bp.below.lg} {
            text-align: left;
            margin-left: 1rem;
            width: auto;
        }
    }
`

const StyledDepartments = styled("section")`
    padding: 3rem 0;
`

export const People = props => (
  <PeopleWrapper>
    <FlexAlign justify="center">
      <div>
        {props.title && <h3 className="title">{props.title}</h3>}
      </div>
    </FlexAlign>
    <FlexAlign justify="flex-start">
      <div className="person">
        <div className="image">
          {props.image.url && <img src={prefixImageUrl(props.image.url)} alt={props.name} />}
        </div>
        <div className="info">
          <h2>{props.name}</h2>
          {props.email && (
            <a href={"mailto:" + props.email}>{props.email}</a>
          )}
          {props.phone && <span>Telefon: {props.phone}</span>}
          {props.mobile && <span>Mobil: {props.mobile}</span>}
        </div>
      </div>
    </FlexAlign>
  </PeopleWrapper>
)

const Department = props => {
  console.log(props.peopleSet);
  return (
    <DepartMentWrapper>
      <h2>{props.name}</h2>
      <DepartmentRow>
        {props.peopleSet.map((p, i) => (
          <People key={i} {...p} />
        ))}
      </DepartmentRow>
    </DepartMentWrapper>
  )
}

const DepartMentList = props => {
  return (
    <MaxWidth>
      {props.allDepartments.map((d, i) => {
        return <Department key={i} {...d} />
      })}
    </MaxWidth>
  )
}

const DepartmentsWrapper = () => {
  const { loading, error, data } = useQuery(query);

  if (loading) return <LoadingPage />;
  if (error) {
    console.error("Error fetching departments:", error);
    return <p>Error loading contact information.</p>;
  }

  return (
    <StyledDepartments>
      {data && <DepartMentList {...data} />}
    </StyledDepartments>
  );
};

const query = gql`
    {
        allDepartments {
            name
            peopleSet {
                name
                email
                phone
                mobile
                title
                image(version: "contact") {
                    url
                }
            }
        }
    }
`

const Contact = ({ page }) => {
  const [form, setForm] = useState({ sendTo: "info@orvelin.se" });
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const [sendEmailMutation, { loading: mutationLoading, error: mutationError }] = useMutation(FORM_MUTATION, {
    onCompleted: (data) => {
      if (data?.sendEmail?.ok) {
        setSent(true);
        setSubmitError(false);
      } else {
        setSubmitError(true);
      }
    },
    onError: (error) => {
      console.error("Form submission error:", error);
      setSubmitError(true);
    }
  });

  const updateData = (e) => {
    setForm(prevForm => ({
      ...prevForm,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(false);
    if (!form.name || !form.email || !form.subject || !form.message) {
      setSubmitError(true);
      return;
    }
    sendEmailMutation({ variables: { emailData: form } });
  };

  return (
    <Fragment>
      <FormContainer>
        <MaxWidth>
          <div className="content" dangerouslySetInnerHTML={{ __html: page.content }} />
          {sent ? (
            <p>Ditt mail är skickat!</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <FormFlex onChange={updateData}>
                <div>
                  <Input name="name" placeholder="Vad heter du?" required />
                  <Input name="email" type="email" placeholder="Vad är din e-post?" required />
                  <Input name="subject" placeholder="Vad gäller ditt ärende?" required />
                </div>
                <div>
                  <TextArea name="message" placeholder="Berätta mer om vad vi kan hjälpa dig med." required />
                </div>
              </FormFlex>
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                {(submitError || mutationError) && (
                  <p style={{ color: 'red' }}>
                    Något gick fel, har du fyllt i samtliga fält korrekt?
                  </p>
                )}
                <SubmitButton type="submit" disabled={mutationLoading}>
                  {mutationLoading ? "Skickar..." : "Jag är klar, skicka!"}
                </SubmitButton>
              </div>
            </form>
          )}
        </MaxWidth>
      </FormContainer>
      <DepartmentsWrapper />
    </Fragment>
  );
};

const FORM_MUTATION = gql`
    mutation SendMail($emailData: EmailInput!) {
        sendEmail(emailData: $emailData) {
            ok
        }
    }
`

export default Contact
